// Діагностика для телефонів без доступу до консолі: відкрийте сайт з ?debug=1 (вимкнути: ?debug=0).
// Пише перебіг сесії в localStorage й показує на екрані, чим закінчилась ПОПЕРЕДНЯ сесія:
// "pagehide" — сторінку перезавантажили/закрили, немає "pagehide" — вкладку вбив браузер (нестача пам'яті).
const KEY = "askold_dbg";
const FLAG = "askold_dbg_on";
const MAX_LINES = 120;

interface Session { id: string, startedAt: string, ua: string, lines: string[] }

const safe = <T,>(fn: () => T, fallback: T): T => { try { return fn(); } catch { return fallback; } };

// Ізоляція причини падіння: ?noanim=1 вимикає всі анімації, фільтри й маски, ?nobanners=1 ховає банери ящика/розіграшів
const applyIsolation = () => {
    const q = new URLSearchParams(location.search);
    const css: string[] = [];
    // окремо вимикаємо одну групу ефектів: ?kill=anim,filter,mask,blur (будь-яка комбінація)
    const kill = (q.get("kill") || "").split(",");
    if (kill.includes("anim")) css.push("*,*::before,*::after{animation:none!important;transition:none!important}");
    if (kill.includes("filter")) css.push("*,*::before,*::after{filter:none!important}");
    if (kill.includes("mask")) css.push("*,*::before,*::after{mask-image:none!important;-webkit-mask-image:none!important}");
    if (kill.includes("blur")) css.push("*,*::before,*::after{backdrop-filter:none!important;-webkit-backdrop-filter:none!important}");
    // вимкнути анімації лише всередині вказаних блоків: ?stop=.lb-banner|.products (селектори через |)
    (q.get("stop") || "").split("|").filter(Boolean).forEach((sel) =>
        css.push(`${sel},${sel} *,${sel} *::before,${sel} *::after{animation:none!important;transition:none!important}`));
    if (q.get("noanim") === "1") css.push("*,*::before,*::after{animation:none!important;transition:none!important;filter:none!important;mask-image:none!important;-webkit-mask-image:none!important;backdrop-filter:none!important;-webkit-backdrop-filter:none!important}");
    if (q.get("nobanners") === "1") css.push(".lb-banner{display:none!important}");
    if (!css.length) return;
    const el = document.createElement("style");
    el.textContent = css.join("\n");
    document.head.appendChild(el);
};

export const initDebugLog = () => {
    applyIsolation();
    const param = new URLSearchParams(location.search).get("debug");
    if (param === "1") safe(() => localStorage.setItem(FLAG, "1"), undefined);
    if (param === "0") safe(() => { localStorage.removeItem(FLAG); localStorage.removeItem(KEY); }, undefined);
    if (!safe(() => localStorage.getItem(FLAG), null)) return;

    const sessions: Session[] = safe(() => JSON.parse(localStorage.getItem(KEY) || "[]"), []);
    const previous = sessions[sessions.length - 1];
    const current: Session = {
        id: Math.random().toString(36).slice(2, 7),
        startedAt: new Date().toISOString(),
        ua: navigator.userAgent,
        lines: [],
    };
    sessions.push(current);
    while (sessions.length > 3) sessions.shift();

    let box: HTMLDivElement | null = null;
    let collapsed = false;

    const t0 = performance.now();
    const save = () => safe(() => localStorage.setItem(KEY, JSON.stringify(sessions)), undefined);
    const log = (msg: string) => {
        current.lines.push(`${((performance.now() - t0) / 1000).toFixed(1)}s ${msg}`);
        if (current.lines.length > MAX_LINES) current.lines.splice(1, 1);
        save();
        try { render(); } catch { /* екранний звіт — другорядне, лог важливіший */ }
    };

    // ---- події, по яких видно, що сталося ----
    const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    log(`start nav=${nav?.type} url=${location.pathname}${location.search}`);
    window.addEventListener("error", (e) => log(`ERROR ${e.message} @${(e.filename || "").split("/").pop()}:${e.lineno}`));
    window.addEventListener("unhandledrejection", (e) => log(`REJECTION ${String((e.reason && e.reason.message) || e.reason)}`));
    window.addEventListener("pagehide", (e) => log(`pagehide persisted=${(e as PageTransitionEvent).persisted}`));
    window.addEventListener("pageshow", (e) => log(`pageshow persisted=${(e as PageTransitionEvent).persisted}`));
    document.addEventListener("visibilitychange", () => log(`visibility=${document.visibilityState}`));
    window.addEventListener("load", () => log("window load"));
    window.addEventListener("beforeunload", () => log("beforeunload"));

    const origError = console.error;
    console.error = (...args: unknown[]) => {
        log(`console.error ${args.map((a) => (a instanceof Error ? a.message : String(a))).join(" ").slice(0, 200)}`);
        origError.apply(console, args as []);
    };

    // мережа: що запитували і чи відповіли
    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: any[]) {
        const u = String(url).replace(/^https?:\/\/[^/]+/, "").slice(0, 60);
        this.addEventListener("loadend", () => log(`xhr ${method} ${u} -> ${this.status}`));
        return (origOpen as any).call(this, method, url, ...rest);
    } as typeof XMLHttpRequest.prototype.open;

    // «пульс» раз на 2 секунди: якщо він обірвався без pagehide, вкладку вбили
    const beat = () => {
        const imgs = document.images.length;
        const done = [...document.images].filter((i) => i.complete).length;
        log(`beat dom=${document.getElementsByTagName("*").length} img=${done}/${imgs}`);
    };
    setInterval(beat, 2000);

    // ---- екранний звіт ----
    const verdict = () => {
        if (!previous) return "попередньої сесії немає";
        const ended = previous.lines.some((l) => l.includes("pagehide") || l.includes("beforeunload"));
        const last = previous.lines[previous.lines.length - 1] || "";
        return ended
            ? "ПОПЕРЕДНЯ СЕСІЯ: завершилась штатно (pagehide), тобто сторінку перезавантажили"
            : `ПОПЕРЕДНЯ СЕСІЯ: обірвалась БЕЗ pagehide, тобто вкладку вбив браузер. Остання подія: ${last}`;
    };
    const text = () => [
        verdict(),
        previous ? `--- попередня (${previous.id}, ${previous.startedAt}) ---\n${previous.lines.slice(-25).join("\n")}` : "",
        `--- ця (${current.id}) ---\n${current.lines.slice(-25).join("\n")}`,
    ].join("\n");
    function render() {
        if (!document.body) return;
        if (!box) {
            box = document.createElement("div");
            box.style.cssText = "position:fixed;left:0;right:0;bottom:0;z-index:2147483647;max-height:45vh;overflow:auto;background:rgba(0,0,0,.88);color:#9f9;font:10px/1.3 monospace;padding:6px;white-space:pre-wrap;word-break:break-all";
            box.addEventListener("click", () => { collapsed = !collapsed; render(); });
            document.body.appendChild(box);
        }
        box.textContent = collapsed ? `[debug ${current.id}, тапніть щоб розгорнути]` : `[тапніть щоб згорнути] ${text()}`;
    }
    if (document.body) render(); else document.addEventListener("DOMContentLoaded", render);
};
